"use client";

import { useState } from "react";
import {
  ChartStyleFields,
  useChartStyle,
} from "@/components/tool-kit/charts/controls";
import { ChartFigure } from "@/components/tool-kit/charts/figure";
import {
  Notice,
  PillGroup,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY, HINT } from "@/components/tool-kit/styles";
import { ConceptMapDiagram } from "@/components/tools/diagrams-more/concept-map-diagram";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { chartFilename } from "@/lib/tool-kit/charts/layout";
import {
  type ConceptWarning,
  conceptCounts,
  conceptRows,
  conceptSentences,
  layoutConceptMap,
  parseConceptMap,
} from "@/lib/tool-kit/diagrams-more/concept-map";
import type { LayeredDirection } from "@/lib/tool-kit/diagrams-more/layered";

/** English strings. A translated content file overrides these through `ui`. */
export const CONCEPT_MAP_MAKER_STRINGS = {
  inputLabel:
    "Your concept map — one proposition per line: concept, linking phrase, concept",
  inputHint:
    "Write Concept -linking phrase-> Concept, and chain on: Plants -make-> Oxygen -is needed for-> Respiration. Reading each line aloud should give a sentence. The first concept is the top of the map; a line that starts with an arrow continues from the concept that began the line above. Spaced dashes work too: A -- causes --> B.",
  exampleText:
    "Photosynthesis -needs-> Sunlight\nPhotosynthesis -needs-> Carbon dioxide\nPhotosynthesis -needs-> Water\nPhotosynthesis -happens in-> Chloroplasts\nPhotosynthesis -produces-> Glucose\nPhotosynthesis -produces-> Oxygen\nChloroplasts -contain-> Chlorophyll\nChlorophyll -absorbs-> Sunlight\nGlucose -fuels-> Cellular respiration\nCellular respiration -uses-> Oxygen\nCellular respiration -releases-> Carbon dioxide",
  exampleTitle: "Photosynthesis and Respiration",
  loadExample: "Load the example",
  clear: "Clear",
  conceptsDetected: "one:{count} concept|other:{count} concepts",
  linksDetected: "one:{count} link|other:{count} links",
  crossLinksDetected:
    "one:{count} cross-link (dashed)|other:{count} cross-links (dashed)",
  unlabelledNotice:
    "one:{count} link has no linking phrase. Add one between the dashes, as in -causes->, so it reads as a sentence.|other:{count} links have no linking phrase. Add one between the dashes, as in -causes->, so each reads as a sentence.",
  enterConcepts:
    "Type at least one proposition, such as A -causes-> B, to draw the map.",
  directionLabel: "Direction",
  directionDown: "Top to bottom",
  directionRight: "Left to right",
  warnings: {
    selfLink:
      "Line {line}: “{text}” links to itself, so that link was left out.",
    repeat:
      "Line {line}: {text} was already linked, so the repeat was left out.",
    emptyConcept:
      "Line {line}: a link has no concept on one side, so it was ignored.",
    noConceptAbove:
      "Line {line}: this link has no concept above to continue from, so it was ignored.",
    tooMany:
      "Line {line}: “{text}” was left out — the map holds up to {limit} concepts.",
  },
  moreWarnings:
    "one:…and {count} more notice.|other:…and {count} more notices.",
  fromColumn: "Concept",
  phraseColumn: "Linking phrase",
  toColumn: "Concept",
  kindColumn: "Type",
  kindLink: "Link",
  kindCross: "Cross-link",
  labelsAndStyle: "Title and figure style",
  chartTitle: "Title (a focus question works well)",
  apaStyle: "APA style figure",
  apaHint:
    "Takes the title out of the image and sets the figure number in bold and the title in italics above it — the APA 7 layout.",
  figureNumber: "Figure number",
  figureNote: "Note (optional)",
  figureLabel: "Figure {number}",
  noteLabel: "Note.",
  copyCaption: "Copy number and title",
  downloadSvg: "Download SVG",
  downloadPng: "Download PNG",
  copyData: "Copy as sentences",
  copied: "Copied!",
  exportFailed:
    "This browser could not create the PNG. Try Download SVG, or another browser.",
  showDataTable: "Show the map as a table of propositions",
  chartLabel: "Concept map",
  chartDescription:
    "Concept map of {concepts} and {links}, starting from “{first}”.",
} as const;

const CONCEPT_LIMIT = 50;
const SHOWN_WARNINGS = 4;

export default function ConceptMapMakerWidget() {
  const strings = useToolUi(CONCEPT_MAP_MAKER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [raw, setRaw] = useState<string>(strings.exampleText);
  const [direction, setDirection] = useState<LayeredDirection>("down");
  const [style, update] = useChartStyle({
    title: strings.exampleTitle,
    xTitle: "",
    yTitle: "",
    color: "blue",
    valueLabels: false,
    apa: false,
    figureNumber: "1",
    note: "",
  });

  const parse = parseConceptMap(raw, CONCEPT_LIMIT);
  const layout =
    parse.concepts.length > 0 ? layoutConceptMap(parse, direction) : null;
  const counts = conceptCounts(parse);
  const conceptsText = tPlural(
    strings.conceptsDetected,
    locale,
    counts.concepts,
    {
      count: fmt(counts.concepts),
    }
  );
  const linksText = tPlural(strings.linksDetected, locale, counts.links, {
    count: fmt(counts.links),
  });
  const rows = conceptRows(parse).map((row) => [
    row.from,
    row.phrase,
    row.to,
    row.crossLink ? strings.kindCross : strings.kindLink,
  ]);
  const shown = parse.warnings.slice(0, SHOWN_WARNINGS);
  const hidden = parse.warnings.length - shown.length;

  function warningText(warning: ConceptWarning): string {
    return t(strings.warnings[warning.kind], {
      line: fmt(warning.line),
      text: warning.text,
      limit: fmt(CONCEPT_LIMIT),
    });
  }

  return (
    <div className="space-y-6">
      <TextAreaField
        hint={strings.inputHint}
        id="cmm-input"
        label={strings.inputLabel}
        onChange={setRaw}
        prose
        rows={10}
        value={raw}
      />
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setRaw(strings.exampleText)}
          type="button"
        >
          {strings.loadExample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setRaw("")}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      <div aria-live="polite" className="space-y-2">
        {layout ? (
          <p className={HINT}>
            {conceptsText}
            {" · "}
            {linksText}
            {counts.crossLinks > 0
              ? ` · ${tPlural(strings.crossLinksDetected, locale, counts.crossLinks, { count: fmt(counts.crossLinks) })}`
              : ""}
          </p>
        ) : (
          <Notice>{strings.enterConcepts}</Notice>
        )}
        {parse.unlabelled > 0 ? (
          <Notice>
            {tPlural(strings.unlabelledNotice, locale, parse.unlabelled, {
              count: fmt(parse.unlabelled),
            })}
          </Notice>
        ) : null}
        {shown.map((warning) => (
          <Notice key={`${warning.kind}-${warning.line}-${warning.text}`}>
            {warningText(warning)}
          </Notice>
        ))}
        {hidden > 0 ? (
          <p className={HINT}>
            {tPlural(strings.moreWarnings, locale, hidden, {
              count: fmt(hidden),
            })}
          </p>
        ) : null}
      </div>
      <PillGroup
        label={strings.directionLabel}
        name="cmm-direction"
        onChange={setDirection}
        options={[
          { value: "down", label: strings.directionDown },
          { value: "right", label: strings.directionRight },
        ]}
        value={direction}
      />
      {layout ? (
        <ChartFigure
          apa={style.apa}
          data={conceptSentences(parse)}
          figureNumber={style.figureNumber}
          filename={chartFilename(style.title, "concept-map")}
          labels={strings}
          note={style.note}
          table={{
            head: [
              strings.fromColumn,
              strings.phraseColumn,
              strings.toColumn,
              strings.kindColumn,
            ],
            rows,
          }}
          title={style.title}
        >
          <ConceptMapDiagram
            description={t(strings.chartDescription, {
              concepts: conceptsText,
              links: linksText,
              first: parse.concepts[0].text,
            })}
            id="cmm-chart"
            label={style.title.trim() || strings.chartLabel}
            layout={layout}
            title={style.apa ? "" : style.title}
          />
        </ChartFigure>
      ) : null}
      <ChartStyleFields
        idPrefix="cmm"
        labels={strings}
        style={style}
        update={update}
      />
    </div>
  );
}
